/**
 * FLOU, as a printed document knows it.
 *
 * ── WHY THE COLOURS ARE HEX AND NOT `oklch()` ────────────────────────────
 * The site's tokens live in `packages/ui/src/styles/globals.css` in OKLCH,
 * which is the right space for a screen: it keeps chroma even as lightness
 * moves, which is what makes the pink→violet ramp read as one ramp. PDF has no
 * such space. pdfmake takes sRGB, so the ramp is converted ONCE, here, with the
 * values written down beside the tokens they came from — rather than converted
 * again by whoever adds the next document and lands two pinks apart.
 *
 * ── AND WHY THE WORDMARK LIVES HERE ──────────────────────────────────────
 * It used to live in `apps/web/src/lib/logo-svg.ts`, which is where the canvas
 * and WebGL treatments read it from. A document cannot import out of the web
 * app, and a second copy of four bezier paths is a mark that drifts. So the
 * outlines moved down here and the app re-exports them: one set of curves, two
 * renderers, no way for them to disagree.
 *
 * Pure data. No pdfmake, no node, nothing that stops a browser bundling it.
 */

/** `--flou-pink`, `oklch(0.72 0.22 350)`. */
export const FLOU_PINK = '#ff5cb8'
/** `--flou-magenta`, `oklch(0.66 0.25 320)`. The mid-stop, and the accent. */
export const FLOU_MAGENTA = '#d14bee'
/** `--flou-violet`, `oklch(0.62 0.24 285)`. */
export const FLOU_VIOLET = '#7f61ff'

/**
 * The page the site is, printed.
 *
 * `--flou-bg` is `oklch(0.08 0.012 300)`, which converts to `#020103` — so near
 * black that the violet cast is gone and a printer would lay down flat carbon.
 * This is a step up the same hue, chosen so the band reads as the brand's black
 * rather than as any black: dark enough for white lettering at 17pt, light
 * enough that the cast survives both a screen and an office laser.
 */
export const FLOU_BLACK = '#100c18'

/** The text ramp, on paper. Not the site's — white on black inverts badly. */
export const INK = '#1b1920'
export const MUTED = '#59575d'
export const RULE = '#d1d0d4'
export const RULE_SOFT = '#e8e7ea'

/**
 * A document has two skins, and they are not each other's negative.
 *
 * ── WHY NOT JUST INVERT ──────────────────────────────────────────────────
 * The light skin's magenta tints are the brand mixed INTO white at 5 and 12 per
 * cent; the same mix into black at the same percentages is invisible. Contrast
 * is not symmetric either — pure white body copy on near-black reads harder
 * than near-black on white, so the dark skin's running text is white and its
 * secondary text sits higher up the ramp than the light skin's sits down it.
 *
 * ── AND WHY BOTH EXIST ───────────────────────────────────────────────────
 * The dark one is the site's own face: brand black, the ramp across the top,
 * the wordmark in white. It is what somebody opens on a phone and recognises.
 * The light one is what survives a laser printer and a scanner — a page of
 * `#100c18` is a cartridge. The owner asked to see the dark one; both stay,
 * because which is right depends on whether the offer is read or filed.
 */
export interface DocumentPalette {
  /** Full-bleed page colour, or `null` to leave the paper alone. */
  readonly page: string | null
  readonly ink: string
  readonly muted: string
  readonly rule: string
  readonly ruleSoft: string
  /** The table's heading row and its stripes. */
  readonly tintHead: string
  readonly tintRow: string
  /** The contact card. */
  readonly card: string
  /** Micro-caps and the tick beside a heading. */
  readonly accent: string
  /** The same, where it sits ON the brand black. */
  readonly accentOnBand: string
  /** The header band — the same as the page on a dark skin. */
  readonly band: string
  readonly bandInk: string
  /**
   * The small wordmark in the footer.
   *
   * Its own value rather than `rule`, which is what it used to borrow. On
   * paper a hairline grey is right — the mark is a signature, not a second
   * headline. On the dark skin that same grey is `#2f2c36` on `#100c18` and
   * the mark all but disappears; the owner asked for it white, and white is
   * also what the header does with it twelve centimetres above.
   */
  readonly footerMark: string
}

export const LIGHT_PALETTE: DocumentPalette = {
  page: null,
  ink: INK,
  muted: MUTED,
  rule: RULE,
  ruleSoft: RULE_SOFT,
  tintHead: '#f9e9fd',
  tintRow: '#fdf6fe',
  card: '#faf9fb',
  accent: FLOU_MAGENTA,
  accentOnBand: FLOU_PINK,
  band: FLOU_BLACK,
  bandInk: '#ffffff',
  footerMark: RULE,
}

/**
 * The site, printed.
 *
 * Every value is white or the brand magenta composited over `#100c18` at the
 * proportion the site's own tokens use — so the greys carry the violet cast
 * rather than being neutral greys that happen to be dark.
 */
export const DARK_PALETTE: DocumentPalette = {
  page: FLOU_BLACK,
  ink: '#ffffff',
  muted: '#a9a8ac',
  rule: '#2f2c36',
  ruleSoft: '#211d28',
  tintHead: '#3a1a47',
  tintRow: '#1a1621',
  card: '#1c1824',
  accent: FLOU_PINK,
  accentOnBand: FLOU_PINK,
  band: FLOU_BLACK,
  bandInk: '#ffffff',
  footerMark: '#ffffff',
}

export type DocumentTheme = 'light' | 'dark'

export function paletteFor(theme: DocumentTheme | undefined): DocumentPalette {
  return theme === 'light' ? LIGHT_PALETTE : DARK_PALETTE
}

/**
 * The ramp, let down into paper.
 *
 * A table cannot be filled with `#d14bee` — 9pt black on it is unreadable and a
 * page of it is a highlighter. These are the same magenta mixed into white at
 * 12% and 5%: enough for the header row to read as one object and for the
 * stripes to group a long session table by eye, and light enough that a laser
 * printer lays down almost nothing.
 */
/** The brand ramp, as pdfmake wants a gradient: stops in order. */
export const FLOU_RAMP = [FLOU_PINK, FLOU_MAGENTA, FLOU_VIOLET] as const

/**
 * The wordmark's four outlines — F, L, O, U — as one `<svg>`.
 *
 * `fill="#000"` is baked in and every consumer swaps it: the web wordmark for
 * `currentColor`, a document for whatever it is printing on. Kept as a string
 * rather than as a file so that nothing has to resolve a path at render time —
 * the bundler trap that `pdf.ts` documents at length.
 */
export const FLOU_WORDMARK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 434.37 121.54">
  <g fill="#000">
    <path d="M103.02,47.05c.4,18.31-14.65,33.36-33.32,33.43l-36.27.14-.1,40.91C15.16,121.42.12,107.37.09,89.27L0,31.17C-.03,14.54,13.46.12,30.6.11L196,0c-.1,17.94-14.33,33.28-32.71,33.29l-124.2.09c-3.13,0-5.66,2.53-5.67,5.66v2.29c0,3.13,2.53,5.68,5.66,5.68l63.93.02Z" />
    <path d="M195.41,88.58c17.91.92,31.36,15.09,31.54,32.61h-127.43s-.06-40.63-.06-40.63c-.28-18.72,15.22-33.23,32.61-33.08l-.04,33.33c0,4.29,3.47,7.78,7.77,7.77h55.62Z" />
    <path d="M286.84,121.49l-53.51.04c.16-22.12-17.84-40.71-40.96-40.5l.06-48.34C192.46,14.6,207.22.47,225.06,0h54.55c17.34.63,32.58,14.33,32.62,32.15l.13,63.75c.03,14.11-11.4,25.57-25.51,25.58ZM264,88.29c8.81-.58,14.84-7.27,15.19-15.67l-.04-39.62-39.49-.09c-7.54,1.33-13.33,6.7-13.84,14.31v41.03s38.19.04,38.19.04Z" />
    <path d="M434.37.24l-.09,88.93c-.02,16.48-15,31.45-33.04,31.5l-82.37.23V33.45c-.02-18.26,15.26-32.92,35.21-33.22v82.47c0,3.13,2.75,5.67,6.13,5.67l32.87.02c3.39,0,6.14-2.54,6.14-5.67V.35s35.15-.11,35.15-.11Z" />
  </g>
</svg>`

/** Intrinsic viewBox of the wordmark. */
export const FLOU_WORDMARK_VIEWBOX = { width: 434.37, height: 121.54 }

/** The mark, painted in one colour. */
export function flouWordmark(color: string): string {
  return FLOU_WORDMARK_SVG.replace('fill="#000"', `fill="${color}"`)
}
